import adareHero from '../assets/projects/adare-collection/hero-map.jpg';
import adareDetail from '../assets/projects/adare-collection/property-detail.jpg';
import adareEnquiry from '../assets/projects/adare-collection/enquiry-form.jpg';

import keymotionHero from '../assets/projects/keymotion/hero.png';
import keymotionCodeScroll from '../assets/projects/keymotion/pdf/code-window-scroll.jpg';
import keymotionAiText from '../assets/projects/keymotion/pdf/ai-assistant-text.jpg';

import healthcareHero from '../assets/projects/healthcare/pdf/microscope-start.jpg';
import healthcarePetri from '../assets/projects/healthcare/pdf/petri-end.jpg';
import healthcareKeyboard from '../assets/projects/healthcare/pdf/keyboard-end.jpg';

import contrastHero from '../assets/projects/contrast/contrast-security-thumbnail.webp';
import contrastNormal from '../assets/projects/contrast/contrast-normal-img.webp';
import contrastMiddle from '../assets/projects/contrast/contrast-middle-img.webp';

import travelperkHero from '../assets/projects/travelperk/pdf/summit.jpg';
import travelperkListen from '../assets/projects/travelperk/pdf/listen-three-words.jpg';
import travelperkChat from '../assets/projects/travelperk/pdf/chat-icons.jpg';

import evoHero from '../assets/projects/evo-agency/pdf/billboard.jpg';
import evoLaptop from '../assets/projects/evo-agency/laptop.webp';
import evoIphone from '../assets/projects/evo-agency/iphone.webp';

import dstilleryHero from '../assets/projects/dstillery/pdf/logo-lockup.jpg';
import dstilleryCubes from '../assets/projects/dstillery/pdf/cubes-explode.jpg';
import dstilleryCopy from '../assets/projects/dstillery/pdf/copy-text.jpg';

import varietalHero from '../assets/projects/varietal/varietal-middle-img.webp';
import varietalGif1 from '../assets/projects/varietal/pdf/gif1-mid.jpg';
import varietalGif2 from '../assets/projects/varietal/pdf/gif2-mid.jpg';

export const PORTFOLIO_CONTACT = {
  name: 'Sean Derham',
  role: 'Designer & Developer',
  location: 'London, UK',
  email: 'sjderham@protonmail.com',
  site: 'seanderham.com',
  linkedin: 'https://www.linkedin.com/in/seanderham/',
  linkedinLabel: 'linkedin.com/in/seanderham',
};

export const PORTFOLIO_ABOUT =
  'Designer and developer with four years delivering brand, motion and digital work for clients, from concept through to launch. I craft immersive visuals and brand-driven experiences across motion graphics, interactive design and web, and use AI as leverage in production. Comfortable owning a project end to end — sole web designer for The Adare Collection, and founder of keymotion.ai.';

/** Homepage projects only — one A4 landscape sheet each. All media are stills. */
export const portfolioProjects = [
  {
    id: 'adare',
    title: 'The Adare Collection',
    href: 'https://theadarecollection.com/',
    hrefLabel: 'theadarecollection.com',
    description: [
      'Sole web designer for The Adare Collection — luxury private rentals near Adare Manor for Ryder Cup 2027. I own the brand on the web end to end: visual system, property pages, enquiry flow, SEO and shipping when new houses come online.',
      'One property template drives the homepage grid, listings, detail pages, map pins and enquiry picker. The site has to feel high-end and convert serious commercial enquiries, not look like a listing portal.',
    ],
    credits: ['Design', 'Web', 'SEO', 'Lead Gen'],
    images: {
      large: {
        src: adareHero,
        alt: 'Adare Collection homepage split hero with map',
      },
      small: [
        { src: adareDetail, alt: 'Range View property detail page' },
        { src: adareEnquiry, alt: 'Qualified enquiry form' },
      ],
    },
  },
  {
    id: 'keymotion',
    title: 'Keymotion',
    href: 'https://www.keymotion.ai/',
    hrefLabel: 'keymotion.ai',
    tallHero: true,
    description: [
      'I founded and built keymotion.ai, an in-browser editor for AI motion graphics made for editors and motion designers.',
      'The product takes a prompt or image/video reference, drafts motion with AI, then lets you finish it in a real keyframe editor before export. I designed and shipped the full stack myself: browser motion editor, AI generation, template library, project files for After Effects, billing and credits.',
    ],
    credits: ['Product', 'AI Motion', 'Editor', 'Full Stack'],
    images: {
      large: { src: keymotionHero, alt: 'keymotion.ai generate interface' },
      small: [
        {
          src: keymotionCodeScroll,
          alt: 'Keymotion code window scroll editor',
          objectPosition: 'calc(50% - 3px) center',
        },
        { src: keymotionAiText, alt: 'Introducing our new AI assistant' },
      ],
    },
  },
  {
    id: 'healthcare',
    title: 'Healthcare',
    description: [
      'This work includes 3D medical animations and scientific visualisation, intended for internal use and specialist presentations within global pharmaceutical organisations.',
    ],
    credits: ['3D Medical Animation', 'Scientific Visualisation', 'Virtual Production'],
    images: {
      large: { src: healthcareHero, alt: '3D microscope visualisation start frame' },
      small: [
        { src: healthcarePetri, alt: 'Petri dish animation end frame' },
        { src: healthcareKeyboard, alt: 'Laboratory keyboard end frame' },
      ],
    },
  },
  {
    id: 'contrast',
    title: 'Contrast Security',
    description: [
      'Working directly with Contrast Security to produce a video featuring senior leadership discussing their cutting-edge security solutions.',
      'Developed and animated 3D glass Möbius strip, created MOGRTs and constructed video from raw footage.',
    ],
    credits: ['3D Design', 'Animation', 'MOGRTs'],
    images: {
      large: { src: contrastHero, alt: 'Contrast Security project thumbnail' },
      small: [
        { src: contrastNormal, alt: 'Contrast Möbius strip render' },
        { src: contrastMiddle, alt: 'Contrast Security lower case study still' },
      ],
    },
  },
  {
    id: 'travelperk',
    title: 'TravelPerk',
    description: [
      'TravelPerk teamed up with me to bring their new brand to life through sleek motion graphics.',
      'Designed for internal events and summits, these animations helped unite teams around a bold, refreshed identity.',
    ],
    credits: ['Motion Graphics', 'Brand Animation'],
    images: {
      large: { src: travelperkHero, alt: 'TravelPerk summit motion still' },
      small: [
        {
          src: travelperkListen,
          alt: 'TravelPerk Listen Learn Lead',
          objectPosition: 'left center',
        },
        { src: travelperkChat, alt: 'TravelPerk chat icons grid' },
      ],
    },
  },
  {
    id: 'evo',
    title: 'Evo Agency',
    description: [
      'I teamed up with Evo Agency to craft a dynamic animation for their brand overhaul, working directly with the CEO and lead designer.',
      "From storyboarding to 3D animation, post-production to editing, I created a video that captured the essence of Evo Agency's new, all-encompassing approach to ecommerce.",
    ],
    credits: ['Storyboarding', '3D Animation', 'Post-production'],
    images: {
      large: { src: evoHero, alt: 'Evo Agency billboard animation still' },
      small: [
        { src: evoLaptop, alt: 'Evo Agency website on laptop' },
        { src: evoIphone, alt: 'Evo Agency website on iPhone' },
      ],
    },
  },
  {
    id: 'dstillery',
    title: 'Dstillery',
    description: [
      'I worked with Dstillery to create a logo animation that aligned with their expertise in AI-powered programmatic advertising.',
      'The animation focused on clean, purposeful motion that brings their logo to life, reinforcing their commitment to precision and data-driven insights.',
    ],
    credits: ['Logo Animation', 'Motion Design'],
    images: {
      large: { src: dstilleryCubes, alt: 'Dstillery logo with cubes exploding outwards' },
      small: [
        { src: dstilleryHero, alt: 'Dstillery logo lockup' },
        {
          src: dstilleryCopy,
          alt: 'Dstillery AI targeting copy still',
          letterboxWhite: true,
          letterboxScale: 0.6,
        },
      ],
    },
  },
  {
    id: 'varietal',
    title: 'Varietal',
    description: [
      'Created the assembly journey of the Varietal armchair, showcasing the modern craftsmanship of a timeless furniture piece: the chair.',
      'This personal project allowed me to refine my 3D modelling, animation, and storytelling skills, using Blender, After Effects, and Premiere Pro.',
    ],
    credits: ['3D Modelling', 'Animation', 'Storytelling'],
    images: {
      large: { src: varietalHero, alt: 'Varietal armchair assembly still' },
      small: [
        { src: varietalGif1, alt: 'Varietal assembly mid still one' },
        { src: varietalGif2, alt: 'Varietal assembly mid still two' },
      ],
    },
  },
];
